import { DETAIL_PATH } from '@/helpers/offering';
import type { OfferingByType, OfferingType } from '@/services/offering';
import type { Class } from '@/state/class-groups';
import type { GiftCard } from '@/state/gift-cards';
import type { Tenant } from '@/state/tenant';

// Here rather than in page-states.tsx, which reads it too: that module is
// 'use client', and a server module importing a value from one gets a client
// reference instead of the string, so the metadata would carry no title.
export const NOT_FOUND_TITLE = 'Could not find that page';
// The same holds for class-canceled-state.tsx.
export const CANCELED_CLASS_TITLE = 'This class was canceled';

const LIST_PAGES = Object.values(DETAIL_PATH);

// The pages a studio's catalog lives on. Every other page (account, checkout,
// orders, waitlist, claim, terms, top-ups) is noindex whatever the studio
// chose: none of them says anything to a visitor arriving from search.
export const INDEXABLE_PAGES = [
  '/',
  ...LIST_PAGES,
  ...LIST_PAGES.map((path) => `${path}/[slug]`),
];

// The only reader of `indexable`. An older backend does not send it, and the
// studio stays as findable as it was before the field existed.
export const isIndexable = (tenant: Pick<Tenant, 'indexable'>) =>
  tenant.indexable !== false;

type ListedItem = Pick<Class, 'status' | 'availability' | 'slug'>;

const isListed = (item: ListedItem) =>
  item.status === 'ACTIVE' && item.availability === 'PUBLIC';

// The public grouped lists send no group status: the backend returns ACTIVE
// groups only. A status that does arrive is honoured.
const isActiveGroup = (group: object) =>
  !('status' in group) || group.status === 'ACTIVE';

// Items are filtered here as well as by the backend, so a change on either
// side cannot put a hidden offering on the sitemap.
export const listedSlugs = <G extends object>(
  groups: G[],
  itemsOf: (group: G) => ListedItem[]
) =>
  groups
    .filter(isActiveGroup)
    .flatMap(itemsOf)
    .filter(isListed)
    .map((item) => item.slug);

// Gift cards come ungrouped, and the public list sends no status: the backend
// lists ACTIVE ones only. Availability is filtered here as well, as above.
export const listedGiftCardSlugs = (
  giftCards: Pick<GiftCard, 'availability' | 'slug'>[]
) =>
  giftCards
    .filter((giftCard) => giftCard.availability === 'PUBLIC')
    .map((giftCard) => giftCard.slug);

type TypedOffering = {
  [T in OfferingType]: { type: T; offering: OfferingByType[T] };
}[OfferingType];

// A gift card's only picture is an uploaded card face; a themed one has none.
export const offeringImage = ({ type, offering }: TypedOffering) => {
  if (type !== 'gift_card') return offering.image;
  return offering.look.type === 'image' ? offering.look.card_image_url : null;
};

// The public gift card projection carries no status: the lookup by slug
// answers an ACTIVE gift card or 404. A class reads the list's own rule
// (has_listed_times), so its page turns noindex as the sitemap drops it.
export const isActiveOffering = ({ type, offering }: TypedOffering) =>
  type === 'gift_card' ||
  (offering.status === 'ACTIVE' &&
    (type !== 'class' || offering.has_listed_times !== false));

const CONTROL_CHARACTER = /[\u0000-\u001f\u007f]/;

// Checked on the decoded value, the form Next hands a page its params in. Each
// refusal is a slug no offering can hold, and also one that would change the
// backend path it is spliced into: `/`, `?` and `#` start a new segment, query
// or fragment, `..` climbs out of it, and `availability` is the class route
// that lists slots rather than a class.
const isOfferingSlug = (type: OfferingType, slug: string) =>
  slug.length >= 2 &&
  slug.length <= 255 &&
  slug !== '..' &&
  !/[/?#]/.test(slug) &&
  !CONTROL_CHARACTER.test(slug) &&
  !(type === 'class' && slug === 'availability');

// Next's router answers 400 for a malformed escape in the URL itself, but the
// path can also arrive as a caller's own x-pathname header on a route the
// middleware matcher skips, so a slug that does not decode is not found.
const decodeSlug = (segment: string) => {
  try {
    return decodeURIComponent(segment);
  } catch {
    return null;
  }
};

// Null for a path that is not an offering page. `slug` is null for one that is,
// but names nothing an offering could be called, so the answer is not found
// without asking the backend. A slug that passes is decoded: encode it with
// encodeURIComponent wherever it goes back into a URL or a backend path.
export const parseOfferingPath = (
  pathname: string
): { type: OfferingType; slug: string | null } | null => {
  const [root, prefix, segment, ...rest] = pathname.split('/');
  if (root !== '' || segment === undefined || rest.length > 0) return null;

  const entry = Object.entries(DETAIL_PATH).find(
    ([, path]) => path === `/${prefix}`
  );
  if (!entry) return null;

  const type = entry[0] as OfferingType;
  const slug = decodeSlug(segment);
  return {
    type,
    slug: slug !== null && isOfferingSlug(type, slug) ? slug : null,
  };
};

export const isIndexablePage = (pathname: string) =>
  pathname === '/' ||
  LIST_PAGES.includes(pathname) ||
  !!parseOfferingPath(pathname)?.slug;

const LIST_TITLES: Record<OfferingType, string> = {
  class: 'Classes',
  pack: 'Class packs',
  subscription: 'Memberships',
  gift_card: 'Gift cards',
};

export type PageTitleKind = OfferingType | 'offering' | 'not_found';

// `|` rather than `·`: the house copy rule keeps `·` for peers of equal weight,
// and a page and the studio it belongs to are not peers.
export function pageTitle(
  kind: 'offering',
  studio: string,
  name: string
): string;
export function pageTitle(
  kind: Exclude<PageTitleKind, 'offering'>,
  studio: string
): string;
export function pageTitle(kind: PageTitleKind, studio: string, name?: string) {
  if (kind === 'not_found') return NOT_FOUND_TITLE;
  return `${kind === 'offering' ? name : LIST_TITLES[kind]} | ${studio}`;
}
